import styled from "styled-components";
import { typographyMixin } from "../../../design-system/tokens/typography";

export const Strip = styled.section`
  display: flex;
  flex: 0 0 ${({ theme }) => theme.spacing.base[36]};
  min-width: 0;
  width: 100%;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[6]};
  padding: ${({ theme }) => theme.spacing.base[3]};
  overflow: hidden;
  box-sizing: border-box;
  border: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  background: ${({ theme }) => theme.colors.background.primary};
`;

export const Label = styled.h2`
  flex: 0 0 auto;
  margin: 0;
  ${typographyMixin("subtitle", 2)}
  line-height: ${({ theme }) => theme.typography.heading.h3.lineHeight};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const HistoryActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[2]};
`;

export const HistoryButton = styled.button`
  display: inline-flex;
  width: ${({ theme }) => theme.spacing.base[6]};
  height: ${({ theme }) => theme.spacing.base[6]};
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.base[1]};
  box-sizing: border-box;
  border: 0;
  border-radius: calc(
    ${({ theme }) => theme.borders.radius.md} +
      ${({ theme }) => theme.borders.width[1]}
  );
  background: ${({ theme }) => theme.colors.grey[400]};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  svg {
    width: ${({ theme }) => theme.spacing.base[3]};
    height: ${({ theme }) => theme.spacing.base[3]};
    stroke-width: 1.5;
  }

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.text.interactive};
  }

  &:focus-visible {
    outline: ${({ theme }) => theme.borders.width[2]}
      ${({ theme }) => theme.borders.style.solid}
      ${({ theme }) => theme.colors.primary[500]};
    outline-offset: ${({ theme }) => theme.borders.width[1]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

export const Stages = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  height: calc(
    ${({ theme }) => theme.spacing.base[28]} +
      ${({ theme }) => theme.spacing.base[1]} +
      ${({ theme }) => theme.spacing.base[0.5]}
  );
  align-items: center;
  gap: calc(
    ${({ theme }) => theme.spacing.base[2]} +
      ${({ theme }) => theme.spacing.base[0.5]}
  );
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Card = styled.button<{ $selected: boolean }>`
  display: flex;
  flex: 0 0 calc(
    ${({ theme }) => theme.spacing.base[40]} +
      ${({ theme }) => theme.spacing.base[2]}
  );
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.base[2]};
  padding: ${({ theme }) => theme.spacing.base[3]};
  box-sizing: border-box;
  appearance: none;
  border: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  background: ${({ theme }) => theme.colors.background.primary};
  box-shadow: ${({ $selected, theme }) =>
    $selected ? theme.shadows.interactive.focus : "none"};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.border.strong};
  }

  &:focus-visible {
    outline: ${({ theme }) => theme.borders.width[2]}
      ${({ theme }) => theme.borders.style.solid}
      ${({ theme }) => theme.colors.primary[500]};
    outline-offset: ${({ theme }) => theme.borders.width[1]};
  }
`;

export const Icon = styled.span<{ $selected: boolean }>`
  display: inline-flex;
  flex: 0 0 auto;
  width: ${({ theme }) => theme.spacing.base[11]};
  height: ${({ theme }) => theme.spacing.base[11]};
  align-items: center;
  justify-content: center;
  border-radius: calc(
    ${({ theme }) => theme.borders.radius.lg} +
      ${({ theme }) => theme.borders.width[1]}
  );
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.background.tertiary : theme.colors.grey[200]};
  color: ${({ $selected, theme }) =>
    $selected ? theme.colors.text.interactive : theme.colors.text.tertiary};

  svg {
    width: ${({ theme }) => theme.spacing.base[6]};
    height: ${({ theme }) => theme.spacing.base[6]};
    stroke-width: 1.5;
  }
`;

export const StageCopy = styled.span`
  display: flex;
  min-width: 0;
  max-width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const Title = styled.span`
  max-width: 100%;
  overflow: hidden;
  ${typographyMixin("body", 2)}
  color: ${({ theme }) => theme.colors.text.primary};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Metadata = styled.span`
  display: flex;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  gap: calc(
    ${({ theme }) => theme.spacing.base[1]} +
      ${({ theme }) => theme.spacing.base[0.5]}
  );
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

export const MetadataSeparator = styled.span`
  width: ${({ theme }) => theme.spacing.base[1]};
  height: ${({ theme }) => theme.spacing.base[1]};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  background: ${({ theme }) => theme.colors.text.tertiary};
`;

export const Connector = styled.span`
  display: inline-flex;
  flex: 0 0 ${({ theme }) => theme.spacing.base[14]};
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.interactive};

  svg {
    width: ${({ theme }) => theme.spacing.base[14]};
    height: ${({ theme }) => theme.spacing.base[5]};
    stroke-width: 1.5;
  }
`;
